/**
 * Composite Reputation module
 * -------------------------------------------------------------
 * Backend reputation API helpers with a small cache and the tier
 * display mappings used by the auth context.
 *
 * Public API (exported):
 *   - fetchReputation(address: string): Promise<ReputationData>
 *   - getTierColor(tier: string): string
 *   - getTierName(tier: string): string
 *   - invalidateCache(address?: string)
 *
 * No React deps – can be used from hooks or services alike.
 */

import { buildApiUrl, authFetchWithRetry } from './api.js';

// Removed legacy enrich endpoint - no longer supported
import { logger } from './logger.js';

const _cache = new Map();

// ============================================================================
// Backend Reputation API
// ============================================================================

/**
 * Tier color mapping for UI display.
 * Maps reputation tiers to their visual representation colors.
 */
export const TIER_COLORS = {
  'Untrustworthy': '#d62828',    // Red
  'Questionable': '#f77f00',     // Orange
  'Neutral': '#6c757d',          // Gray
  'Respected': '#cd7f32',        // Bronze
  'Trusted': '#c0c0c0',          // Silver
  'Legendary': '#ffd700',        // Gold
  // Fallback aliases for different naming conventions
  'street_rookie': '#d62828',
  'corner_hustler': '#f77f00',
  'block_boss': '#6c757d',
  'borough_king': '#cd7f32',
  'city_legend': '#ffd700',
};

/**
 * Tier display names mapping.
 */
export const TIER_NAMES = {
  'Untrustworthy': 'Street Rookie',
  'Questionable': 'Corner Hustler',
  'Neutral': 'Block Boss',
  'Respected': 'Borough King',
  'Trusted': 'City Legend',
  'Legendary': 'Underworld Legend',
  // Fallback aliases
  'street_rookie': 'Street Rookie',
  'corner_hustler': 'Corner Hustler',
  'block_boss': 'Block Boss',
  'borough_king': 'Borough King',
  'city_legend': 'City Legend',
};

/**
 * Get color for a reputation tier.
 * @param {string} tier - The reputation tier
 * @returns {string} Hex color code
 */
export function getTierColor(tier) {
  return TIER_COLORS[tier] || TIER_COLORS['Neutral'];
}

/**
 * Get display name for a reputation tier.
 * @param {string} tier - The reputation tier
 * @returns {string} Human-readable tier name
 */
export function getTierName(tier) {
  return TIER_NAMES[tier] || tier || 'Unknown';
}

/**
 * Fetch player reputation from backend API.
 * 
 * Returns comprehensive reputation data including:
 * - overall_score: 0.0 to 1.0
 * - tier: Reputation tier name
 * - tier_color: Hex color for the tier
 * - factor_scores: Breakdown by factor
 * - benefits: Active benefits (loan discounts, etc.)
 * 
 * @param {string} walletAddress - Player's wallet address
 * @returns {Promise<Object>} Reputation data
 */
export async function fetchReputation(walletAddress) {
  if (!walletAddress) {
    throw new Error('Wallet address required');
  }

  // Check if API key is available - if not, return default reputation
  const apiKey = localStorage.getItem('dcw_api_key') || localStorage.getItem('dcw_token');
  if (!apiKey) {
    logger.log('[reputation] No API key available, returning default reputation');
    return {
      wallet_address: walletAddress,
      overall_score: 0.5,
      tier: 'Neutral',
      tier_color: TIER_COLORS['Neutral'],
      tier_name: TIER_NAMES['Neutral'],
      factor_scores: {},
      benefits: { loan_discount: 0, max_loan: 500 },
    };
  }

  try {
    const response = await authFetchWithRetry(
      buildApiUrl(`/api/v1/reputation/${walletAddress}`)
    );

    if (!response.ok) {
      if (response.status === 404) {
        // User not found - return default neutral reputation
        return {
          wallet_address: walletAddress,
          overall_score: 0.5,
          tier: 'Neutral',
          tier_color: TIER_COLORS['Neutral'],
          tier_name: TIER_NAMES['Neutral'],
          factor_scores: {},
          benefits: { loan_discount: 0, max_loan: 500 },
        };
      }
      throw new Error(`Reputation fetch failed: ${response.status}`);
    }

    const data = await response.json();
    
    // Enrich with color and display name
    return {
      ...data,
      tier_color: getTierColor(data.tier),
      tier_name: getTierName(data.tier),
    };
  } catch (error) {
    logger.error('[reputation] Failed to fetch reputation:', error);
    // Return neutral fallback on error
    return {
      wallet_address: walletAddress,
      overall_score: 0.5,
      tier: 'Neutral',
      tier_color: TIER_COLORS['Neutral'],
      tier_name: TIER_NAMES['Neutral'],
      factor_scores: {},
      benefits: { loan_discount: 0, max_loan: 500 },
      _error: error.message,
    };
  }
}

/**
 * Fetch reputation history/events for a player.
 * @param {string} walletAddress - Player's wallet address
 * @param {number} limit - Number of events to fetch (default 50)
 * @returns {Promise<Array>} Array of reputation events
 */
export async function fetchReputationHistory(walletAddress, limit = 50) {
  if (!walletAddress) {
    throw new Error('Wallet address required');
  }

  try {
    const response = await authFetchWithRetry(
      buildApiUrl(`/api/v1/reputation/${walletAddress}/history?limit=${limit}`)
    );

    if (!response.ok) {
      throw new Error(`Reputation history fetch failed: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('[reputation] Failed to fetch history:', error);
    return [];
  }
}

/**
 * Fetch reputation leaderboard.
 * @param {number} limit - Number of top players to fetch
 * @returns {Promise<Array>} Leaderboard entries
 */
export async function fetchReputationLeaderboard(limit = 100) {
  try {
    const response = await authFetchWithRetry(
      buildApiUrl(`/api/v1/leaderboard/reputation?limit=${limit}`)
    );

    if (!response.ok) {
      throw new Error(`Leaderboard fetch failed: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('[reputation] Failed to fetch leaderboard:', error);
    return [];
  }
}

export function invalidateCache(address) {
  if (address) return _cache.delete(address);
  _cache.clear();
} 